import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { sanitizeHtml } from "../../../lib/richText";

/**
 * An A4 sheet at 72dpi - the size each page is laid out at before it is
 * scaled to the column. Type, margins and rules all shrink together, so a
 * narrower column is a smaller sheet rather than the same sheet with the
 * words rewrapped: what is on the right is the page a client gets.
 */
const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
/** The sheet's margin on every side, and so the width the words run at. */
const PAGE_MARGIN = 56;
const CONTENT_WIDTH = PAGE_WIDTH - PAGE_MARGIN * 2;
const CONTENT_HEIGHT = PAGE_HEIGHT - PAGE_MARGIN * 2;
/**
 * What the frame spends on itself around the sheet: the stack's scrollbar,
 * which stands beside the sheet rather than over it, and the frame's own
 * border either side. Left out of the sum, the sheet is two pixels wider
 * than the room inside the border, and its right edge is clipped.
 */
const SCROLLBAR = 8;
const FRAME_BORDER = 2;
const FRAME_CHROME = SCROLLBAR + FRAME_BORDER;

/** Tailwind's `lg` - the width at which the column becomes a fixed one. */
const LG_BREAKPOINT = 1024;

interface PolicyPagePreviewProps {
  html: string;
  /** Names the pages to a screen reader - "Guidelines preview". */
  label: string;
  /**
   * The section's own name - "GUIDELINES", "POLICY" - set over the document
   * on the first sheet, the way the PDF heads each section. Not part of the
   * text: it is the same whatever is written under it and cannot be edited.
   */
  title: string;
}

/**
 * One thing that moves to a page as a whole: a paragraph, a heading, or a
 * single item of a list. A list is split into its items so a long one can
 * run over the foot of a page onto the next, the way it would in print; the
 * items are put back into a list of the same kind when the page is built.
 */
interface Unit {
  html: string;
  height: number;
  /** Set on a list item: which kind of list it came from, and its number. */
  list?: { tag: "ul" | "ol"; index: number };
}

const outerHeight = (element: Element): number => {
  const style = getComputedStyle(element);
  return (
    element.getBoundingClientRect().height +
    parseFloat(style.marginTop) +
    parseFloat(style.marginBottom)
  );
};

/** The document broken into the pieces a page is filled with, measured. */
const unitsOf = (container: HTMLElement): Unit[] => {
  // A bare run of text at the top level - which the editor can leave behind
  // a deleted paragraph - is given a paragraph, so it can be measured and
  // moved like everything else.
  for (const node of Array.from(container.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      const p = document.createElement("p");
      container.replaceChild(p, node);
      p.appendChild(node);
    }
  }

  const units: Unit[] = [];
  for (const element of Array.from(container.children)) {
    const tag = element.tagName.toLowerCase();
    if (tag === "ul" || tag === "ol") {
      const start = Number(element.getAttribute("start") ?? 1);
      Array.from(element.children).forEach((item, offset) => {
        units.push({
          html: item.outerHTML,
          height: outerHeight(item),
          list: { tag, index: start + offset },
        });
      });
      continue;
    }
    units.push({ html: element.outerHTML, height: outerHeight(element) });
  }
  return units;
};

/**
 * The pieces dealt onto pages, in order, each page taking what fits. A
 * piece taller than a page - one very long paragraph - gets a page of its
 * own and is clipped at its foot, the way a printer would. The first page
 * has `titleHeight` less room than the rest: the section title stands over
 * the document there.
 */
const paginate = (units: Unit[], titleHeight: number): Unit[][] => {
  const pages: Unit[][] = [[]];
  let used = titleHeight;
  for (const unit of units) {
    const page = pages[pages.length - 1];
    if (page.length > 0 && used + unit.height > CONTENT_HEIGHT) {
      pages.push([unit]);
      used = unit.height;
    } else {
      page.push(unit);
      used += unit.height;
    }
  }
  return pages;
};

/** A page's pieces back into markup, with runs of items rejoined as lists. */
const renderPage = (units: Unit[]): string => {
  let html = "";
  let open: Unit["list"] | undefined;
  for (const unit of units) {
    const sameList = open && unit.list && open.tag === unit.list.tag;
    if (open && !sameList) {
      html += `</${open.tag}>`;
      open = undefined;
    }
    if (unit.list && !open) {
      // A numbered list that continues from the previous page keeps
      // counting rather than starting again at 1.
      const start =
        unit.list.tag === "ol" && unit.list.index > 1
          ? ` start="${unit.list.index}"`
          : "";
      html += `<${unit.list.tag}${start}>`;
      open = unit.list;
    }
    html += unit.html;
  }
  if (open) html += `</${open.tag}>`;
  return html;
};

/**
 * The section's name, as the PDF sets it: small caps in the brand's teal,
 * letter-spaced, over the document's own first heading. One component, so
 * the copy that is measured and the copy that is drawn cannot differ.
 *
 * The space under it is the space a document heading leaves under itself -
 * `h1`'s 0.45em at its 1.85em size - so the title sits over the first
 * heading exactly as that heading sits over its first paragraph.
 */
const SectionTitle: React.FC<{ children: string }> = ({ children }) => (
  <div className="mb-[0.83em] text-[14px] font-semibold uppercase tracking-[0.12em] text-[#0A8A9C]">
    {children}
  </div>
);

/**
 * The document on A4 pages, as many as it takes, each shown whole and
 * scaled to the column's width. Nothing is put on a page that would run off
 * its foot - it goes to the next one, so what is on the right is the stack
 * of sheets a client gets rather than one endless sheet.
 *
 * The split is worked out by drawing the whole document once, off screen,
 * at the page's own width, and reading how tall each piece came out. That
 * is re-done whenever the text changes and whenever the measuring box
 * re-flows - the web font swapping in after first paint, mostly.
 */
const PolicyPagePreview: React.FC<PolicyPagePreviewProps> = ({
  html,
  label,
  title,
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  // Whether the sheet is being fitted to the column's height - from `lg`
  // up - which is also when the frame takes the sheet's width for its own.
  const [fitted, setFitted] = useState(false);
  const [pages, setPages] = useState<string[]>([]);

  const safe = useMemo(() => sanitizeHtml(html), [html]);

  // The sheet is sized to whichever of the column's width or height runs
  // out first, the way the invoice beside the Details form is, so one whole
  // page is on screen at a time and the stack is turned page by page.
  //
  // From `lg` the column is sized by this frame, so its width cannot also be
  // what sizes the frame: the ceiling comes from the `max-w-*` classes on it,
  // which keeps the breakpoint sizes in the stylesheet. Below `lg` the frame
  // is as wide as the page and only its width limits the sheet.
  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const measure = () => {
      const heightBound = window.innerWidth >= LG_BREAKPOINT;
      setFitted(heightBound);

      // Fractional: rounding the box up by half a pixel is enough to clip
      // the sheet's edge.
      const { width, height } = frame.getBoundingClientRect();
      const ceiling = parseFloat(getComputedStyle(frame).maxWidth);
      const room = heightBound ? ceiling : width;

      const byWidth = (room - FRAME_CHROME) / PAGE_WIDTH;
      const byHeight = heightBound ? height / PAGE_HEIGHT : Infinity;

      const next = Math.min(byWidth, byHeight);
      setScale(next > 0 && Number.isFinite(next) ? next : 1);
    };

    measure();
    window.addEventListener("resize", measure);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(frame);
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, []);

  // The split. Drawn into the hidden box, measured, and dealt onto pages.
  useLayoutEffect(() => {
    const box = measureRef.current;
    if (!box) return;

    const split = () => {
      box.innerHTML = safe;
      const titleHeight = titleRef.current ? outerHeight(titleRef.current) : 0;
      setPages(paginate(unitsOf(box), titleHeight).map(renderPage));
    };

    split();
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(split);
    observer?.observe(box);
    return () => observer?.disconnect();
  }, [safe]);

  return (
    // The same frame the editor stands in, so the two columns read as a
    // pair. White, like the sheets: the scrollbar's track runs down the
    // frame's right edge beside them, and on a white ground it reads as the
    // page's own margin with the thumb over it, rather than a strip beside
    // the page.
    <div
      ref={frameRef}
      // From `lg` the frame is exactly one sheet and its scrollbar wide, so
      // the column beside the editor is only as wide as the page it shows
      // and the editor has the rest. The ceilings are the ones the invoice
      // preview uses, so the two screens' pages match.
      style={fitted ? { width: PAGE_WIDTH * scale + FRAME_CHROME } : undefined}
      className="relative flex flex-1 flex-col overflow-hidden rounded-md border border-line bg-white lg:min-h-0 lg:max-w-[400px] xl:max-w-[440px] 2xl:max-w-[480px] 3xl:max-w-[540px] 4xl:max-w-[620px]"
    >
      {/* Where the document is drawn to be measured: the page's own width,
          the page's own type, and out of sight. `aria-hidden` and off the
          labelled stack below, so it is not read as a second copy. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="rich-text pointer-events-none absolute left-0 top-0 invisible text-justify"
        style={{ width: CONTENT_WIDTH }}
      />
      {/* The title drawn once more, out of sight, to learn how much of the
          first page it takes. */}
      <div
        ref={titleRef}
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 invisible"
        style={{ width: CONTENT_WIDTH }}
      >
        <SectionTitle>{title}</SectionTitle>
      </div>

      {/* The stack of sheets, and the one thing here that scrolls: the
          frame is exactly the column, so the wheel over a page moves the
          pages and nothing outside them. */}
      <div
        ref={boxRef}
        aria-label={label}
        className="flex min-h-0 flex-1 flex-col items-start gap-3 overflow-x-hidden overflow-y-auto custom-scrollbar-overlay"
      >
        {pages.map((page, index) => (
          <div
            key={index}
            style={{ width: PAGE_WIDTH * scale, height: PAGE_HEIGHT * scale }}
            className="flex-shrink-0"
          >
            {/* Laid out at full size and shrunk with a transform, which is
                what keeps every measurement on it in proportion - and what
                makes the split above true of what is drawn. */}
            <div
              style={{
                width: PAGE_WIDTH,
                height: PAGE_HEIGHT,
                padding: PAGE_MARGIN,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
              }}
              className="relative overflow-hidden border-b border-line bg-white"
            >
              {index === 0 && <SectionTitle>{title}</SectionTitle>}
              {/* Justified, as the PDF sets it - the editor stays ragged,
                  where a straight right edge would hide double spaces. The
                  first block on a sheet sits at the sheet's margin - or
                  under the title - without its own space above, so the gap
                  under the title is the title's alone. */}
              <div
                className="rich-text text-ink text-justify [&>:first-child]:mt-0"
                dangerouslySetInnerHTML={{ __html: page }}
              />
              <span className="absolute bottom-6 right-[56px] text-[10px] text-ink-subtle">
                Page {index + 1} of {pages.length}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PolicyPagePreview;
